import { metadataH2 } from "app/brands/components/BrandMetadataPanel"
import { RouteMgr } from "app/common/routes"
import { CategoryForestVM2 } from "app/models/category"
import { Link } from "blitz"
import { array } from "fp-ts"
import { pipe } from "fp-ts/function"
import useTranslation from "next-translate/useTranslation"
import { FC } from "react"
import { apply, tw } from "twind"
import { css } from "twind/css"

interface CategoryPageLinkTreeProps {
  categories: CategoryForestVM2
}

const CategoryTreeRecursive: FC<CategoryPageLinkTreeProps> = ({ categories }) => {
  return (
    <ul
      className={tw(
        `mt-1`,
        `flex(& col) gap-1`,
        css`
          padding-left: 0;
        `
      )}
    >
      {pipe(
        categories,
        array.map((c) => {
          const hasSubcategories = array.isNonEmpty(c.subcategories)

          return (
            <li
              className={tw(
                `block`,
                `relative`,
                css`
                  border-left: 2px solid #ddd;

                  &:last-child {
                    border-color: transparent;
                    &::before {
                      border-radius: 0 0 0 2px;
                    }
                  }

                  &::after {
                    content: "";
                    position: absolute;
                    display: block;
                    border: solid #ddd;
                    border-width: 0 0 2px 2px;
                    height: 1.35em;
                    left: -0.1rem;
                    top: -0.25rem;
                    width: 1.05rem;
                    z-index: 0;
                  }
                `
              )}
              key={c.slugPath.join("/")}
            >
              <Link href={RouteMgr.CategoryPage({ slugPath: c.slugPath })}>
                <a
                  className={tw(
                    `blogLink`,
                    `text-xs font-semibold`,
                    `bg-gray-100`,
                    `inline-block`,
                    `p-2`,
                    `relative`,
                    css`
                      &:before,
                      &:after {
                        content: "";
                        position: absolute;
                        top: 0;
                        bottom: 0;
                        border-style: solid;
                        ${apply`border-gray-100`}
                        border-width: 1rem 0 1rem calc(1rem / 2);
                      }

                      &:before {
                        right: 100%;
                        ${apply`border-gray-100`}
                        border-left-color: transparent;
                      }

                      &:after {
                        left: 100%;
                        border-color: transparent;
                        border-left-color: #f3f4f6;
                      }
                    `
                  )}
                >
                  {c.name}
                </a>
              </Link>
              {hasSubcategories && <CategoryTreeRecursive categories={c.subcategories} />}
            </li>
          )
        })
      )}
    </ul>
  )
}

interface CategoryPageLinkTreeProps {
  categories: CategoryForestVM2
  showTitle?: boolean
}

export const CategoryPageLinkTree: FC<CategoryPageLinkTreeProps> = ({ categories, showTitle }) => {
  const { t } = useTranslation()

  return (
    <nav
      className={tw(
        css`
          li {
            ${apply`pl-4`}
          }

          ul {
            ${apply`ml-3`}
          }
        `
      )}
    >
      {showTitle && <h2 className={tw(metadataH2)}>{t("categories:categories")}</h2>}

      <CategoryTreeRecursive categories={categories} />
    </nav>
  )
}
